import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { postMapGame } from '../../utils/mapGameApi';
import LeaderboardLayout, { dedupeByUserIdBestScore, normalizeLeaderboardMatch } from './LeaderboardLayout';

function toSlug(v) {
  return String(v || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function parsePositiveRank(v) {
  const n = parseInt(String(v ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function CategoryLeaderboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category') || '';
  const { user } = useSelector((s) => s.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((s) => s.theme);

  const [matches, setMatches] = useState([]);
  const [currentUserMeta, setCurrentUserMeta] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(true);

  const categoryLabel = useMemo(() => {
    const id = categoryId;
    const cats = Array.isArray(themeData?.categories) ? themeData.categories : [];
    const found = cats.find(
      (c) => c.id === id || toSlug(c.id) === toSlug(id) || toSlug(c.name) === toSlug(id)
    );
    return found?.name?.replace(/\s*Mode\s*$/i, '') || id || 'Category';
  }, [themeData, categoryId]);

  useEffect(() => {
    if (!categoryId) {
      navigate('/categories', { replace: true });
    }
  }, [categoryId, navigate]);

  useEffect(() => {
    if (!categoryId) return undefined;
    if (!user || !adminToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setFetchError('');
      setLoading(true);
      try {
        const uid = user.userId || user.id;
        const data = await postMapGame(adminToken, '/mapCategoryLeaderboard', {
          userId: uid,
          email: user.email,
          name: user.name,
          categoryId: toSlug(categoryId),
        });
        if (cancelled) return;
        if (!data.success) {
          setMatches([]);
          setCurrentUserMeta(null);
          setFetchError(data.error || data.message || 'Could not load category leaderboard');
          return;
        }
        let raw = Array.isArray(data.matches) ? data.matches : [];
        if (raw.length === 0 && data.currentUser) {
          const cu = data.currentUser;
          const id = cu.userId ?? cu.id;
          if (id != null && String(id).trim() !== '') {
            raw = [{ ...cu, userId: id, isSelf: true, matchPercent: 0 }];
          }
        }
        const normalized = raw.map(normalizeLeaderboardMatch);
        setMatches(dedupeByUserIdBestScore(normalized));
        const cu = data.currentUser;
        const apiRank = data.currentUserRank;
        if (cu && (cu.userId != null || cu.id != null)) {
          const cuNorm = normalizeLeaderboardMatch({ ...cu, userId: cu.userId ?? cu.id, isSelf: true });
          setCurrentUserMeta({
            rank: parsePositiveRank(apiRank),
            score: cuNorm.score ?? 0,
            time: cuNorm.time,
            name: cuNorm.name || 'You',
          });
        } else {
          setCurrentUserMeta(
            parsePositiveRank(apiRank)
              ? { rank: parsePositiveRank(apiRank), score: 0, time: '00:00', name: 'You' }
              : null
          );
        }
      } catch (e) {
        console.error('mapCategoryLeaderboard', e);
        if (!cancelled) {
          setMatches([]);
          setCurrentUserMeta(null);
          setFetchError(e.message || 'Network error loading category leaderboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken, categoryId]);

  const footer = (
    <div className="lb-feedback-wrap">
      <Link to="/categories" replace className="lb-feedback-link">
        Next
      </Link>
    </div>
  );

  if (!categoryId) {
    return null;
  }

  return (
    <LeaderboardLayout
      title={`Leaderboard · ${categoryLabel}`}
      matches={matches}
      loading={loading}
      fetchError={fetchError}
      user={user}
      currentUserMeta={currentUserMeta}
      themeData={themeData}
      footer={footer}
      emptyMessage="No one has finished this category in your session yet."
      errorHint="Check that the API is running and the category id matches saved progress."
    />
  );
}
